import type { IHttpClient, HttpResponse } from '@wix/fe-essentials-viewer-platform/http-client'
import type { FedopsLogger } from '@wix/fe-essentials-viewer-platform/fedops'
import { getPublicCodeConfig } from '@wix/ambassador-code-v1-public-code-config-http/http'
import type { SiteMonitoringMessage } from '../formatters/convertToSiteMonitoringMessage'
import { EventId } from 'eventid'

const SCRIPT_ERROR = 'Script error.'
const MAX_LOGS_PER_BATCH = 10
const LOG_SENDING_INTERVAL = 500
// NOTE: This needs to be synched with the message wix-code-viewer-app sends when it runs the user code.
const IGNORED_MESSAGE_REGEX =
	/^Loading the code for the .+\. To debug this code, open .+ in your browser's Developer Tools\.$/

const IS_SITE_MONITORING_ENABLED_INTERACTION_ENDED_CODES = {
	SUCCESS: 0,
	INVALID_RESPONSE: 1,
	EXCEPTION: 2,
}

export type SiteMonitoringServiceOptions = {
	baseUrl: string
	metaSiteId: string
	instance: string
	siteUrl: string
	viewMode: string
	revision: string
	fedopsLogger: FedopsLogger
	httpClient: IHttpClient
	pageDetails: { pageId: string; pageName: string } | null
}

export type SiteMonitoringService = {
	send(message: SiteMonitoringMessage): void
	clear(): void
}

export const createSiteMonitoringService = ({
	baseUrl,
	metaSiteId,
	instance,
	siteUrl,
	viewMode,
	revision,
	fedopsLogger,
	httpClient,
	pageDetails,
}: SiteMonitoringServiceOptions): SiteMonitoringService => {
	const customParams = {
		baseUrl,
		metaSiteId,
		instance,
		siteUrl,
		viewMode,
		revision,
	}

	const isResponseOk = (response: HttpResponse<any>) => response.status >= 200 && response.status < 300

	const isSiteMonitoringEnabled = async (): Promise<boolean> => {
		try {
			fedopsLogger.interactionStarted('is-site-monitoring-enabled-new-api', { customParams })

			const response = await httpClient.request(getPublicCodeConfig({}), {
				signedInstance: instance,
			})

			if (!isResponseOk(response)) {
				fedopsLogger.interactionEnded('is-site-monitoring-enabled-new-api', {
					customParams: {
						...customParams,
						code: IS_SITE_MONITORING_ENABLED_INTERACTION_ENDED_CODES.INVALID_RESPONSE,
						status: response.status,
					},
				})

				return false
			}

			fedopsLogger.interactionEnded('is-site-monitoring-enabled-new-api', {
				customParams: {
					...customParams,
					code: IS_SITE_MONITORING_ENABLED_INTERACTION_ENDED_CODES.SUCCESS,
				},
			})

			return response.data?.publicCodeConfig?.telemetryData?.hasSinks ?? false
		} catch (e) {
			fedopsLogger.interactionEnded('is-site-monitoring-enabled-new-api', {
				customParams: {
					...customParams,
					code: IS_SITE_MONITORING_ENABLED_INTERACTION_ENDED_CODES.EXCEPTION,
					e,
				},
			})

			return false
		}
	}

	const logs: Array<object> = []
	const isSiteMonitoringEnabledPromise = isSiteMonitoringEnabled()
	const operationId = new EventId()
	const insertId = new EventId()
	const sourceLocation = pageDetails ? { file: `pages/${pageDetails.pageName}.${pageDetails.pageId}.js` } : null

	const interval = setInterval(async () => {
		if (logs.length > 0) {
			if (await isSiteMonitoringEnabledPromise) {
				const batch = []

				for (let i = 0; i < MAX_LOGS_PER_BATCH; i++) {
					if (logs.length === 0) {
						break
					}

					batch.push(logs.shift())
				}

				const message = { messages: batch }

				fedopsLogger.interactionStarted('report-events-batch', { customParams })

				const telemetryUrl = `${baseUrl}/_api/wix-code-telemetry-collector/v1/telemetry-messages`

				httpClient
					.post(telemetryUrl, message)
					.then(() => {
						fedopsLogger.interactionEnded('report-events-batch', {
							customParams: {
								...customParams,
								code: IS_SITE_MONITORING_ENABLED_INTERACTION_ENDED_CODES.SUCCESS,
							},
						})
					})
					.catch((e: unknown) => {
						fedopsLogger.interactionEnded('report-events-batch', {
							customParams: {
								...customParams,
								code: IS_SITE_MONITORING_ENABLED_INTERACTION_ENDED_CODES.EXCEPTION,
								e,
							},
						})
					})
			}
		}
	}, LOG_SENDING_INTERVAL)

	return {
		send({ message, severity }: SiteMonitoringMessage) {
			if (message === SCRIPT_ERROR || IGNORED_MESSAGE_REGEX.test(message)) {
				return
			}

			logs.push({
				insertId: insertId.new(),
				timestamp: new Date().toISOString(),
				severity,
				labels: {
					siteUrl,
					namespace: 'Velo',
					tenantId: metaSiteId,
					viewMode,
					revision,
					...(pageDetails ? { pageName: pageDetails.pageName } : {}),
				},
				operation: {
					id: operationId.new(),
					producer: siteUrl,
					first: false,
					last: false,
				},
				sourceLocation,
				jsonPayload: {
					message,
				},
			})
		},
		clear() {
			clearInterval(interval)
		},
	}
}
